import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import N550BaseMap from "./components/N550BaseMap";
import ContextBar from "./components/ContextBar";
import SummaryPanel from "./components/SummaryPanel";
import HistoricalBanner from "./components/HistoricalBanner";
import SegmentDetailPanel from "./components/SegmentDetailPanel";
import {
  TimeFilterProvider,
  useTimeFilter,
} from "./contexts/TimeFilterContext";
import { N550_SEGMENTS } from "./mock/n550Segments.mock";
import { getIsadForTimeRange } from "./mock/n550Isad.mock";
import { getIncidentsForSegment } from "./mock/n550Incidents.mock";
import { usePolling } from "./hooks/usePolling";
import {
  fetchWeatherZones,
  fetchTrafficFlow,
  fetchOBUProbes,
} from "./mock/context.mock";
import { fetchSegmentDetail } from "./mock/segmentDetail.mock";

const N550MapContent = () => {
  const [selectedSegmentId, setSelectedSegmentId] = useState(null);
  const [areaSelectedIds, setAreaSelectedIds] = useState([]);
  const [segmentDetail, setSegmentDetail] = useState(null);
  const [segmentDetailLoading, setSegmentDetailLoading] = useState(false);
  const [selectedIncidentId, setSelectedIncidentId] = useState(null);
  const zoomToRef = useRef(null);
  const panToRef = useRef(null);
  const { dateRange, mode } = useTimeFilter();

  const isadData = getIsadForTimeRange(dateRange);

  const { data: weatherZones } = usePolling(fetchWeatherZones, 15_000);
  const { data: obuProbes } = usePolling(fetchOBUProbes, 15_000);

  const fetchTrafficForMode = useCallback(
    () => fetchTrafficFlow(mode),
    [mode]
  );
  const { data: trafficData } = usePolling(fetchTrafficForMode, 60_000);

  const conditionsData = useMemo(
    () => ({
      weatherZones: weatherZones ?? [],
      trafficFlow:
        mode === "live" && trafficData?.segments ? trafficData.segments : {},
      obuProbes: obuProbes ?? [],
    }),
    [weatherZones, trafficData, obuProbes, mode]
  );

  const segmentIncidents = useMemo(
    () => getIncidentsForSegment(selectedSegmentId, mode),
    [selectedSegmentId, mode]
  );

  useEffect(() => {
    if (!selectedSegmentId) {
      setSegmentDetail(null);
      return;
    }
    let cancelled = false;
    setSegmentDetailLoading(true);
    fetchSegmentDetail(selectedSegmentId).then((data) => {
      if (!cancelled) {
        setSegmentDetail(data);
        setSegmentDetailLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedSegmentId]);

  const handleSegmentClick = (id) => {
    setSelectedSegmentId(id);
    setAreaSelectedIds([]);
    setSelectedIncidentId(null);
    const segment = N550_SEGMENTS.find((s) => s.id === id);
    if (segment?.path?.length) {
      const mid = segment.path[Math.floor(segment.path.length / 2)];
      panToRef.current?.(mid.lat, mid.lng);
    }
  };

  const handleAreaSelect = (ids) => {
    setAreaSelectedIds(ids);
    setSelectedSegmentId(null);
    setSelectedIncidentId(null);
  };

  const handleClosePanel = () => {
    setSelectedSegmentId(null);
    setAreaSelectedIds([]);
    setSelectedIncidentId(null);
  };

  const handleIncidentClick = useCallback((id) => {
    setSelectedIncidentId(id);
  }, []);

  const handleIncidentBack = useCallback(() => {
    setSelectedIncidentId(null);
  }, []);

  const handleZoomToLocation = useCallback((lat, lng) => {
    zoomToRef.current?.(lat, lng);
  }, []);

  const handleActivateLiveView = useCallback(() => {}, []);

  const handleSegmentFocus = useCallback(
    (segmentId) => {
      const segment = N550_SEGMENTS.find((s) => s.id === segmentId);
      if (segment?.path?.length) {
        const lats = segment.path.map((p) => p.lat);
        const lngs = segment.path.map((p) => p.lng);
        handleZoomToLocation(
          lats.reduce((a, b) => a + b, 0) / lats.length,
          lngs.reduce((a, b) => a + b, 0) / lngs.length
        );
      }
      setSelectedSegmentId(segmentId);
      setAreaSelectedIds([]);
      setSelectedIncidentId(null);
    },
    [handleZoomToLocation]
  );

  const detailOpen = !!selectedSegmentId || areaSelectedIds.length > 0;

  return (
    <div className="flex h-[calc(100dvh-172px)] w-full flex-col gap-3 overflow-hidden">
      <ContextBar
        onZoomToLocation={handleZoomToLocation}
        onActivateLiveView={handleActivateLiveView}
      />
      <HistoricalBanner />

      <div className="flex min-h-0 flex-1 gap-3 md:gap-4">
        <div className="min-w-0 flex-1">
          <div className="h-full w-full">
            <N550BaseMap
              segments={N550_SEGMENTS}
              isadData={isadData}
              selectedSegmentId={selectedSegmentId}
              onSegmentClick={handleSegmentClick}
              onAreaSelect={handleAreaSelect}
              zoomToRef={zoomToRef}
              panToRef={panToRef}
              conditionsData={conditionsData}
              incidents={segmentIncidents}
              selectedIncidentId={selectedIncidentId}
              onIncidentClick={handleIncidentClick}
            />
          </div>
        </div>

        <div className="relative w-full shrink-0 overflow-hidden rounded-xl md:w-[320px]">
          <div
            className="flex h-full transition-transform duration-300 ease-in-out"
            style={{
              width: "200%",
              transform: detailOpen ? "translateX(-50%)" : "translateX(0)",
            }}
          >
            <div className="h-full w-1/2 pr-1.5">
              <SummaryPanel
                isadData={isadData}
                onSegmentFocus={handleSegmentFocus}
              />
            </div>

            <div className="h-full w-1/2 pl-1.5">
              {selectedSegmentId ? (
                <SegmentDetailPanel
                  segmentId={selectedSegmentId}
                  detail={segmentDetail}
                  loading={segmentDetailLoading}
                  isOpen={detailOpen}
                  onClose={handleClosePanel}
                  onSegmentFocus={handleSegmentFocus}
                  incidents={segmentIncidents}
                  selectedIncidentId={selectedIncidentId}
                  onIncidentClick={handleIncidentClick}
                  onIncidentBack={handleIncidentBack}
                />
              ) : (
                <aside className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm dark:border-white/40 dark:bg-navy-800">
                  <div className="border-b border-gray-200 px-4 pb-3 pt-3 dark:border-white/10">
                    <button
                      onClick={handleClosePanel}
                      className="mb-1 flex items-center gap-1 text-xs text-gray-400 transition-colors hover:text-brand-500 dark:hover:text-brand-400"
                      aria-label="Back to summary"
                    >
                      <span>‹</span>
                      <span>Corridor Summary</span>
                    </button>
                    <p className="text-sm font-semibold text-navy-700 dark:text-white">
                      Area Selection
                    </p>
                  </div>
                  <div className="p-4 text-sm text-gray-600 dark:text-gray-400">
                    <p className="mb-2 font-medium text-navy-700 dark:text-white">
                      {areaSelectedIds.length} segment
                      {areaSelectedIds.length !== 1 ? "s" : ""} selected
                    </p>
                    <ul className="space-y-1">
                      {areaSelectedIds.map((id) => (
                        <li
                          key={id}
                          className="font-mono text-xs text-gray-400"
                        >
                          {id}
                        </li>
                      ))}
                    </ul>
                  </div>
                </aside>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const N550Map = () => (
  <TimeFilterProvider>
    <N550MapContent />
  </TimeFilterProvider>
);

export default N550Map;
